/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useContext, useCallback, useEffect, ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStores } from '../stores';
import { Interview } from '../types/interview';
import { observer } from 'mobx-react-lite';

interface InterviewContextType {
  interview: Interview | null;
  loading: boolean;
  error: string | null;
  submitRecording: () => Promise<void>;
  sendVideoChunk: (chunk: Blob, options?: { timeout?: number }) => Promise<void>;
  probeConnection: (timeoutMs?: number) => Promise<void>;
}

const InterviewContext = createContext<InterviewContextType | undefined>(undefined);

export const useInterview = () => {
  const context = useContext(InterviewContext);
  if (context === undefined) {
    throw new Error('useInterview must be used within an InterviewProvider');
  }
  return context;
};

interface InterviewProviderProps {
  children: ReactNode;
}

export const InterviewProvider = observer(({ children }: InterviewProviderProps) => {
  const { interviewStore } = useStores();
  const params = useParams();
  const navigate = useNavigate();

  const initInterview = useCallback(async () => {
    try {
      if (interviewStore.currentInterview) {
        return;
      }
      if (!params.interviewId) {
        return;
      }
      await interviewStore.startInterview(params.interviewId);
    } catch (error) {
      console.error('Error initializing interview:', error);
    }
  }, [params.interviewId, interviewStore]);

  // Вызываем инициализацию при монтировании компонента
  useEffect(() => {
    initInterview();
  }, [initInterview]);

  const submitRecording = async () => {
    if (!interviewStore.currentInterview?.id) return;

    try {
      // Отправляем видео и получаем обновленное интервью
      await interviewStore.updateInterview(interviewStore.currentInterview.id);

      // Проверяем статус обновленного интервью
      const updatedInterview = interviewStore.currentInterview;
      if (!updatedInterview) {
        throw new Error('Не удалось получить обновленные данные интервью');
      }

      // Если интервью завершено, перенаправляем на страницу результатов
      if (updatedInterview.finish) {
        navigate(`/video-interview/${updatedInterview.id}/result`);
        return; // Прерываем выполнение, так как происходит перенаправление
      }
    } catch (error) {
      console.error('Error submitting recording:', error);
      throw error;
    }
  };

  const sendVideoChunk = useCallback(
    async (chunk: Blob, options?: { timeout?: number }) => {
      if (!chunk || chunk.size === 0) {
        return;
      }
      if (!interviewStore.currentInterview?.id) {
        return;
      }
      await interviewStore.sendVideoChunk(chunk, options);
    },
    [interviewStore]
  );

  const probeConnection = useCallback(
    async (timeoutMs = 10_000) => {
      const id = interviewStore.currentInterview?.id;
      if (!id) {
        throw new Error('Интервью не инициализировано');
      }
      // await interviewApi.probeInterviewConnection(id, timeoutMs);
    },
    [interviewStore]
  );

  const value = {
    interview: interviewStore.currentInterview,
    loading: interviewStore.loading,
    error: interviewStore.error,
    submitRecording,
    sendVideoChunk,
    probeConnection
  };

  return <InterviewContext.Provider value={value}>{children}</InterviewContext.Provider>;
});
